// On-screen Quick Report: pick a period, see the numbers, search and sort them, and download exactly what is shown.
// The download asks the server for the same report with the same period, so the CSV contains the viewer's data.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import axiosInstance from "../services/axiosInstance";
import { handleApiError } from "./apiErrorHandler";
import { ymdToDate } from "../utils/dateOnly";
import useIsMobile from "../hooks/useIsMobile";
import { defaultChoice, reportParams } from "./quickReports";

const money = (v) => `Rs. ${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const whole = (v) => Number(v || 0).toLocaleString("en-US");
const prettyDate = (s) => {
    const d = ymdToDate(s);
    return d ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
};
const isIdColumn = (c) => /\bid$/i.test(c.label.trim());        // internal ids stay in the CSV but are not shown to the owner
const toValue = (v, type) => (type === "money" || type === "int" ? Number(v) || 0 : v ?? "");

const QuickReportViewer = ({ report, visible, onHide, canDownload }) => {
    const isMobile = useIsMobile();
    const orgExpiry = useMemo(() => {
        try { return Number(JSON.parse(localStorage.getItem("organizationSettings") || "{}").expiry_reminder_days) || null; } catch (e) { return null; }
    }, []);
    const [choice, setChoice] = useState(() => (report ? defaultChoice(report, orgExpiry) : {}));   // a fresh viewer per report (see key in Home)
    const reqId = useRef(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [error, setError] = useState(null);

    const ready = report && (report.picker !== "range" || choice.preset !== "custom" || (choice.from && choice.to));

    const load = useCallback(async () => {
        if (!report || !ready || (!Object.keys(choice).length && report.picker !== "none")) return;
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        try {
            const { data: d } = await axiosInstance.get(report.endpoint, { params: { ...reportParams(report, choice), format: "json" } });
            if (id === reqId.current) setData(d);               // ignore an older answer that arrives late
        } catch (err) {
            if (id !== reqId.current) return;
            setError("Could not load this report. Please try again.");
            handleApiError(err);
        } finally {
            if (id === reqId.current) setLoading(false);
        }
    }, [report, choice, ready]);

    useEffect(() => { if (visible) load(); }, [visible, load]);

    const download = async () => {
        try {
            const res = await axiosInstance.get(report.endpoint, { params: reportParams(report, choice), responseType: "blob" });
            const cd = res.headers?.["content-disposition"] || "";
            const m = cd.match(/filename="?([^"]+)"?/i);
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            link.download = (m && m[1]) || data?.filename || "report.csv";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            handleApiError(err);
        }
    };

    const columns = useMemo(() => data?.columns || [], [data]);
    const shownColumns = useMemo(() => columns.map((c, i) => ({ ...c, i })).filter((c) => !isIdColumn(c)), [columns]);
    const rows = useMemo(() => (data?.rows || []).map((r) => Object.fromEntries(columns.map((c, i) => [`c${i}`, toValue(r[i], c.type)]))), [data, columns]);
    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? rows.filter((r) => Object.values(r).some((v) => String(v).toLowerCase().includes(q))) : rows;
    }, [rows, search]);
    // totals cover the WHOLE period (computed by the server, since only the first rows are sent); while searching they cover the matches
    const searching = search.trim() !== "";
    const totals = useMemo(() => columns.map((c, i) => {
        if (data?.totals?.[i] == null) return null;                // the server says which columns are summable
        const value = !searching && data?.totals?.[i] != null ? Number(data.totals[i]) : shown.reduce((s, r) => s + (r[`c${i}`] || 0), 0);
        return { label: c.label, type: c.type, value };
    }).filter(Boolean), [columns, shown, data, searching]);

    const periodText = data?.period ? `${prettyDate(data.period.from)} - ${prettyDate(data.period.to)}` : "";
    const presetOptions = report?.picker === "range" ? [...report.presets, ...(report.custom ? [{ key: "custom", label: "Custom dates" }] : [])] : [];

    const body = (c, i) => (row) => {
        const v = row[`c${i}`];
        if (c.type === "money") return money(v);
        if (c.type === "int") return whole(v);
        if (c.type === "date") return prettyDate(v);
        return v;
    };

    return (
        <Dialog header={data?.title || report?.cardLabel || "Report"} visible={visible} onHide={onHide} modal dismissableMask
                className={isMobile ? "p-dialog-maximized" : undefined}
                style={isMobile ? { width: "100vw", margin: 0 } : { width: "min(1050px, 96vw)" }}
                footer={(
                    <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                        <span className="text-sm text-600">{data ? `${whole(data.row_count)} row${data.row_count === 1 ? "" : "s"}` : ""}</span>
                        <div className="flex gap-2">
                            {canDownload && <Button label="Download CSV" icon="pi pi-download" outlined onClick={download} disabled={!data || loading} />}
                            <Button label="Close" icon="pi pi-times" text onClick={onHide} />
                        </div>
                    </div>
                )}>
            <div className={isMobile ? "flex flex-column gap-2 mb-3" : "flex flex-wrap align-items-end gap-3 mb-3"}>
                {report?.picker === "range" && (
                    <div className={isMobile ? "w-full" : undefined}>
                        <label className="block text-sm text-600 mb-1">Period</label>
                        <Dropdown value={choice.preset} options={presetOptions} optionLabel="label" optionValue="key"
                                  onChange={(e) => setChoice((c) => ({ ...c, preset: e.value }))} className={isMobile ? "w-full" : undefined} style={isMobile ? undefined : { minWidth: 220 }} />
                    </div>
                )}
                {report?.groups && (
                    <div className={isMobile ? "w-full" : undefined}>
                        <label className="block text-sm text-600 mb-1">Show</label>
                        <Dropdown value={choice.group} options={report.groups} optionLabel="label" optionValue="key"
                                  onChange={(e) => setChoice((c) => ({ ...c, group: e.value }))} className={isMobile ? "w-full" : undefined} style={isMobile ? undefined : { minWidth: 220 }} />
                    </div>
                )}
                {report?.picker === "range" && choice.preset === "custom" && (
                    <div className={isMobile ? "w-full" : undefined}>
                        <label className="block text-sm text-600 mb-1">Dates</label>
                        <Calendar value={choice.from ? [choice.from, choice.to || null] : null} selectionMode="range" readOnlyInput showIcon dateFormat="yy-mm-dd"
                                  onChange={(e) => setChoice((c) => ({ ...c, preset: "custom", from: e.value?.[0] || null, to: e.value?.[1] || null }))}
                                  className={isMobile ? "w-full" : undefined} style={isMobile ? undefined : { width: 260 }} touchUI={isMobile} />
                    </div>
                )}
                {report?.picker === "days" && (
                    <div className={isMobile ? "w-full" : undefined}>
                        <label className="block text-sm text-600 mb-1">Expiring within</label>
                        <Dropdown value={choice.days} options={[...new Set([...report.days, choice.days].filter(Boolean))].sort((a, b) => a - b).map((d) => ({ label: `${d} days`, value: d }))}
                                  onChange={(e) => setChoice({ days: e.value })} className={isMobile ? "w-full" : undefined} />
                    </div>
                )}
                {periodText && <span className="text-sm text-600" style={{ alignSelf: isMobile ? "flex-start" : "center" }}><i className="pi pi-calendar mr-2" />{periodText}</span>}
                <IconField iconPosition="left" className={isMobile ? "w-full" : "ml-auto"}>
                    <InputIcon className="pi pi-search" />
                    <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search in this report" className={isMobile ? "w-full" : undefined} style={isMobile ? undefined : { width: 240 }} />
                </IconField>
            </div>

            {totals.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    <div className="surface-100 border-round px-3 py-2"><div className="text-xs text-600">Rows</div><b>{whole(searching ? shown.length : data?.row_count ?? shown.length)}</b></div>
                    {totals.map((t) => (
                        <div key={t.label} className="surface-100 border-round px-3 py-2">
                            <div className="text-xs text-600">Total {t.label.replace(/^total\s+/i, "")}</div>
                            <b>{t.type === "money" ? money(t.value) : whole(t.value)}</b>
                        </div>
                    ))}
                </div>
            )}

            {error && <Message severity="error" text={error} className="w-full mb-3" />}
            {loading && <ProgressBar mode="indeterminate" style={{ height: 4 }} className="mb-3" />}
            {data?.truncated && <Message severity="warn" className="w-full mb-3 justify-content-start" text={`Showing the first ${whole(data.rows.length)} of ${whole(data.row_count)} rows. Download the CSV to get all of them.`} />}

            {data && (
                <DataTable value={shown} size="small" stripedRows removableSort scrollable
                           scrollHeight={isMobile ? "max(220px, calc(100vh - 420px))" : "max(260px, calc(100vh - 410px))"}
                           virtualScrollerOptions={{ itemSize: 38 }}
                           emptyMessage={search ? "Nothing matches your search." : "Nothing to show for this period."}>
                    {shownColumns.map((c, pos) => (
                        <Column key={c.i} field={`c${c.i}`} header={c.label} sortable body={body(c, c.i)}
                                alignHeader={c.type === "money" || c.type === "int" ? "right" : "left"}
                                bodyStyle={{ textAlign: c.type === "money" || c.type === "int" ? "right" : "left", whiteSpace: "nowrap", minWidth: isMobile ? "7rem" : undefined }}
                                frozen={isMobile && pos === 0} />
                    ))}
                </DataTable>
            )}
            {data && shown.length > 10 && <div className="text-xs text-600 mt-2">Scroll inside the table to see all {whole(shown.length)} rows.</div>}
        </Dialog>
    );
};

export default QuickReportViewer;
